import React, { useState } from "react";
import Navbar from "../Hero/Navbar/Navbar";
import Footer from "../Footer/Footer";
import {
  FaBrain,
  FaChartLine,
  FaUserFriends,
  FaGamepad,
  FaClock,
  FaFileAlt,
  FaMobileAlt,
  FaLock,
  FaCloudUploadAlt,
  FaTimes
} from "react-icons/fa";

const featuresData = [
  {
    icon: <FaBrain size={30} className="text-white" />,
    title: "AI-Powered Learning",
    description:
      "Get personalized quiz suggestions and insights based on your learning pace and performance using intelligent algorithms.",
    detailedContent: `
      <h3>How AI Enhances Your Learning</h3>
      <p>Our advanced machine learning algorithms analyze your performance patterns, identifying strengths and areas for improvement.</p>
      <ul>
        <li>Personalized question difficulty that adapts as you learn</li>
        <li>Smart content recommendations based on your learning style</li>
        <li>Predictive analytics to optimize your study schedule</li>
      </ul>
      <p>The more you use QUZY, the smarter it gets - creating a truly personalized learning experience that evolves with you.</p>
    `
  },
  {
    icon: <FaChartLine size={30} className="text-white" />,
    title: "Progress Tracking",
    description:
      "Visualize your growth with detailed analytics that track your accuracy, speed, and subject-wise performance over time.",
    detailedContent: `
      <h3>Data-Driven Learning Insights</h3>
      <p>Comprehensive dashboards and visual reports help you understand your learning journey at a glance.</p>
      <ul>
        <li>Interactive performance graphs showing improvement over time</li>
        <li>Subject and topic breakdown to identify knowledge gaps</li>
        <li>Comparative analytics to benchmark against peers</li>
        <li>Exportable reports to share with teachers or mentors</li>
      </ul>
      <p>Transform your study habits with actionable insights that guide your learning strategy.</p>
    `
  },
  {
    icon: <FaUserFriends size={30} className="text-white" />,
    title: "Peer Collaboration",
    description:
      "Connect with friends, challenge peers in quiz battles, and learn better together with a thriving student community.",
    detailedContent: `
      <h3>Learning is Better Together</h3>
      <p>Our social learning features turn studying from a solitary activity into an engaging, collaborative experience.</p>
      <ul>
        <li>Create study groups with shared resources and discussions</li>
        <li>Challenge friends to knowledge battles on any subject</li>
        <li>Participate in community-wide competitions and leaderboards</li>
        <li>Exchange study materials and quiz questions with peers</li>
      </ul>
      <p>Research shows collaborative learning improves retention by up to 50% - experience the difference with QUZY's social features.</p>
    `
  },
  {
    icon: <FaGamepad size={30} className="text-white" />,
    title: "Gamified Quizzes",
    description:
      "Earn points, unlock badges, and level up as you learn – because studying should be as fun as it is effective.",
    detailedContent: `
      <h3>Turn Learning into an Adventure</h3>
      <p>Our gamification elements are designed by educational psychologists to maximize engagement while ensuring effective learning.</p>
      <ul>
        <li>Earn XP and level up your student profile across different subjects</li>
        <li>Unlock achievement badges that showcase your academic prowess</li>
        <li>Complete daily challenges for bonus rewards and streaks</li>
        <li>Participate in themed seasonal events with special content</li>
      </ul>
      <p>QUZY's game mechanics trigger dopamine release associated with reward systems, making learning addictively fun while improving information retention.</p>
    `
  },
  {
    icon: <FaClock size={30} className="text-white" />,
    title: "Smart Scheduling",
    description:
      "Access your digital library and quizzes anytime, with AI-driven scheduling and daily reminders to enhance your learning consistency.",
    detailedContent: `
      <h3>Optimize Your Study Time</h3>
      <p>Our intelligent scheduling system works with your natural learning rhythms and availability to maximize productivity.</p>
      <ul>
        <li>AI-powered study plans based on your learning goals and deadlines</li>
        <li>Spaced repetition algorithms that optimize review timing</li>
        <li>Calendar integration with customizable notifications</li>
        <li>Focus mode timers with break reminders for optimal concentration</li>
      </ul>
      <p>QUZY's scheduling features are built on cognitive science principles that ensure you're studying the right material at the right time for maximum retention.</p>
    `
  },
  {
    icon: <FaFileAlt size={30} className="text-white" />,
    title: "Topic-Wise Resources",
    description:
      "Access curated study material and questions categorized by subject and topic to help you focus on what matters.",
    detailedContent: `
      <h3>Comprehensive Learning Library</h3>
      <p>Our extensive database of educational resources is organized to make finding relevant content quick and intuitive.</p>
      <ul>
        <li>Expertly curated content aligned with major curricula worldwide</li>
        <li>Multi-format resources including text, video, and interactive elements</li>
        <li>Difficulty levels from beginner to advanced for progressive learning</li>
        <li>Regular updates with fresh content and the latest educational standards</li>
      </ul>
      <p>With QUZY's topic-wise organization, you'll always have the right resources at your fingertips, allowing you to focus on learning rather than searching.</p>
    `
  },
];

const FeatureModal = ({ feature, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 relative">
          {/* Close button */}
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
            aria-label="Close"
          >
            <FaTimes size={24} />
          </button>
          
          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="bg-indigo-500 w-12 h-12 flex items-center justify-center rounded-lg mr-4">
              {feature.icon}
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{feature.title}</h3>
          </div>
          
          {/* Content */}
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: feature.detailedContent }}
          />
          
          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-6 rounded-lg transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturePage = () => {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openFeatureModal = (feature) => {
    setSelectedFeature(feature);
    setIsModalOpen(true);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeFeatureModal = () => {
    setIsModalOpen(false);
    // Re-enable body scrolling
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <Navbar />
      <section className="py-16 bg-white">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-300 uppercase opacity-10 mb-2">
            Features
          </h2>
          <h3 className="text-4xl font-bold -mt-8 relative z-10 bg-gradient-to-r from-indigo-600 to-indigo-400 bg-clip-text text-transparent">
            FEATURES
          </h3>
          <p className="text-gray-600 mt-2 px-4">
            Explore the tools and technology that make QUZY the ultimate quiz-based learning platform.
          </p>
        </div>

        {/* Main Feature Highlight */}
        <div className="max-w-6xl mx-auto px-4 mb-16">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-400 text-white rounded-xl p-8 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4">Revolutionize Your Study Habits</h2>
                <p className="mb-6">
                  QUZY combines cutting-edge AI technology with proven learning methodologies to create a study experience that's not just effective, but actually enjoyable.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <div className="bg-white bg-opacity-20 p-1 rounded-full mr-2">
                      <FaBrain className="text-white" />
                    </div>
                    <span>Adaptive learning that grows with you</span>
                  </li>
                  <li className="flex items-center">
                    <div className="bg-white bg-opacity-20 p-1 rounded-full mr-2">
                      <FaGamepad className="text-white" />
                    </div>
                    <span>Gamification that keeps you motivated</span>
                  </li>
                  <li className="flex items-center">
                    <div className="bg-white bg-opacity-20 p-1 rounded-full mr-2">
                      <FaUserFriends className="text-white" />
                    </div>
                    <span>Social learning that builds community</span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl shadow-xl transform rotate-3 max-w-xs">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <div className="text-center p-4">
                      <FaBrain size={40} className="text-indigo-600 mx-auto mb-2" />
                      <h3 className="text-indigo-800 font-bold">AI-Powered</h3>
                      <p className="text-sm text-gray-600">Our intelligent system adapts to your unique learning style</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {featuresData.map((feature, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-lg text-center shadow hover:shadow-xl hover:-translate-y-1
                       cursor-pointer transition duration-300 hover:bg-gradient-to-br hover:from-indigo-200 hover:to-white"
              onClick={() => openFeatureModal(feature)}
            >
              <div className="bg-indigo-500 w-12 h-12 mx-auto flex items-center justify-center rounded mb-4">
                {feature.icon}
              </div>
              <h4 className="text-xl font-bold text-gray-700 mb-2">
                {feature.title}
              </h4>
              <p className="text-gray-600 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Feature Modal */}
        {selectedFeature && (
          <FeatureModal 
            feature={selectedFeature} 
            isOpen={isModalOpen} 
            onClose={closeFeatureModal} 
          />
        )}
      </section>
      <Footer />
    </>
  );
};

export default FeaturePage;
